/**
 * Wizard de exportação do AEP — 3 etapas.
 *  1) Layout (orientação/margens/fonte)
 *  2) Seções (toggles de blocos do corpo do AEP)
 *  3) Anexos (anexos opcionais + CTA "Gerar PDF")
 *
 * Mantém o contrato anterior (orient/margin/font/included/onPrint/onClose) para
 * que AEPReport e CnpjAEPReport não precisem mudar nada além do nome.
 */
import { useState } from "react";
import { SECTIONS, MARGIN_MM } from "./shared";

// Sections that act as "anexos opcionais" — grouped on Step 3.
const ANEXO_KEYS = new Set(["recomendacoes_ia", "plano_anual", "absenteismo"]);

const ANEXO_META = {
  recomendacoes_ia: {
    title: "Recomendações Prioritárias",
    description: "Inclui as recomendações geradas pela análise interpretativa da IA — editáveis na tela da avaliação antes de imprimir.",
    icon: "fa-list-check",
  },
  plano_anual: {
    title: "Plano Anual SST",
    description: "Programação anual de campanhas, DDS, inspeções e treinamentos vinculada à empresa. Edite na aba Plano Anual antes de gerar.",
    icon: "fa-calendar-days",
  },
  absenteismo: {
    title: "Relatório de Absenteísmo NR-01",
    description: "KPIs de afastamentos, principais CIDs e distribuição por setor. Útil para correlacionar afastamentos com fatores psicossociais.",
    icon: "fa-heart-pulse",
  },
};

export default function AEPExportWizard({
  orient, setOrient, margin, setMargin, font, setFont,
  included, setIncluded, onClose, onPrint,
}) {
  const [step, setStep] = useState(1);

  const bodySections = SECTIONS.filter((s) => !ANEXO_KEYS.has(s.k));
  const anexoSections = SECTIONS.filter((s) => ANEXO_KEYS.has(s.k));

  const bodySelected = bodySections.filter((s) => included[s.k]).length;
  const anexoSelected = anexoSections.filter((s) => included[s.k]).length;

  const setAllBody = (val) => {
    const next = { ...included };
    bodySections.forEach((s) => { next[s.k] = val; });
    setIncluded(next);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 no-print"
         onClick={onClose} data-testid="aep-export-wizard">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white border w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[88vh] flex flex-col shadow-2xl">

        {/* Header / steps */}
        <div className="p-5 border-b border-slate-200 bg-slate-50">
          <div className="flex justify-between items-start mb-3">
            <div>
              <div className="overline mb-1 text-sollute-green">Wizard de exportação</div>
              <h2 className="font-display text-xl font-extrabold tracking-tight">Gerar AEP em PDF</h2>
              <p className="text-xs text-slate-600 mt-1">3 etapas: Layout · Seções · Anexos.</p>
            </div>
            <button onClick={onClose} className="btn btn-ghost text-sm" data-testid="aep-wizard-close">
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>
          </div>
          <div className="flex items-center gap-2 text-xs">
            {[
              { n: 1, label: "Layout" },
              { n: 2, label: "Seções" },
              { n: 3, label: "Anexos" },
            ].map((s, i) => (
              <div key={s.n} className="flex items-center gap-2 flex-1">
                <button
                  onClick={() => setStep(s.n)}
                  data-testid={`aep-wizard-step-${s.n}`}
                  className={`flex items-center gap-2 flex-1 px-3 py-2 border ${
                    step === s.n
                      ? "border-emerald-600 bg-emerald-50 text-emerald-900 font-bold"
                      : step > s.n
                      ? "border-emerald-300 bg-white text-emerald-700"
                      : "border-slate-200 bg-white text-slate-500"
                  }`}>
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${
                    step >= s.n ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"
                  }`}>{step > s.n ? <i className="fa-solid fa-check text-[10px]"></i> : s.n}</span>
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
                {i < 2 && <span className="text-slate-300">→</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {step === 1 && (
            <>
              <div>
                <label className="label">Orientação</label>
                <div className="grid grid-cols-2 gap-2">
                  {[["retrato", "fa-file", "Retrato"], ["paisagem", "fa-image", "Paisagem"]].map(([k, icon, lbl]) => (
                    <button key={k} onClick={() => setOrient(k)} data-testid={`aep-orient-${k}`}
                            className={`btn ${orient === k ? "btn-primary" : "btn-secondary"} justify-center`}>
                      <i className={`fa-solid ${icon}`}></i> {lbl}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label">Margens</label>
                <div className="grid grid-cols-3 gap-2">
                  {[["estreita", "Estreita"], ["normal", "Normal"], ["larga", "Larga"]].map(([k, lbl]) => (
                    <button key={k} onClick={() => setMargin(k)} data-testid={`aep-margin-${k}`}
                            className={`btn ${margin === k ? "btn-primary" : "btn-secondary"} justify-center text-xs`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                <div className="text-[10px] text-slate-500 mt-1.5">{MARGIN_MM[margin]} em todos os lados</div>
              </div>

              <div>
                <label className="label">Tamanho da fonte</label>
                <div className="grid grid-cols-3 gap-2">
                  {[["pequena", "Pequena"], ["normal", "Normal"], ["grande", "Grande"]].map(([k, lbl]) => (
                    <button key={k} onClick={() => setFont(k)} data-testid={`aep-font-${k}`}
                            className={`btn ${font === k ? "btn-primary" : "btn-secondary"} justify-center text-xs`}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label !mb-0">
                    Seções do corpo do AEP
                    <span className="text-xs text-slate-500 ml-2">({bodySelected}/{bodySections.length})</span>
                  </label>
                  <div className="flex gap-2">
                    <button onClick={() => setAllBody(true)} className="btn btn-ghost text-xs" data-testid="aep-sections-all">Todas</button>
                    <button onClick={() => setAllBody(false)} className="btn btn-ghost text-xs" data-testid="aep-sections-none">Nenhuma</button>
                  </div>
                </div>
                <div className="space-y-1 max-h-[44vh] overflow-y-auto border border-slate-200 bg-white">
                  {bodySections.map((s) => (
                    <label key={s.k}
                           className={`flex items-center gap-2 p-2 cursor-pointer text-sm hover:bg-slate-50 border-b border-slate-100 ${
                             included[s.k] ? "" : "opacity-60"
                           }`}>
                      <input type="checkbox" checked={!!included[s.k]}
                             onChange={() => setIncluded({ ...included, [s.k]: !included[s.k] })}
                             data-testid={`aep-section-${s.k}`} />
                      <span>{s.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div>
                <label className="label">Anexos opcionais ({anexoSelected}/{anexoSections.length})</label>
                <div className="space-y-2.5">
                  {anexoSections.map((s) => {
                    const meta = ANEXO_META[s.k] || { title: s.label, description: "", icon: "fa-paperclip" };
                    const checked = !!included[s.k];
                    return (
                      <label key={s.k}
                             className={`block border cursor-pointer p-3 transition-all ${
                               checked
                                 ? "border-emerald-600 bg-emerald-50/50"
                                 : "border-slate-200 hover:border-slate-400 bg-white"
                             }`}
                             data-testid={`aep-anexo-${s.k}`}>
                        <div className="flex items-start gap-3">
                          <input type="checkbox" checked={checked}
                                 onChange={() => setIncluded({ ...included, [s.k]: !checked })}
                                 className="mt-1.5"
                                 data-testid={`aep-anexo-check-${s.k}`} />
                          <i className={`fa-solid ${meta.icon} text-lg mt-1 ${checked ? "text-emerald-700" : "text-slate-400"}`}></i>
                          <div className="flex-1">
                            <div className="font-bold text-slate-900 text-sm">{meta.title}</div>
                            <div className="text-xs text-slate-600 mt-0.5">{meta.description}</div>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
                <i className="fa-solid fa-circle-info mr-1"></i>
                Ao clicar em <strong>Gerar PDF</strong>, o navegador abre o diálogo de impressão. Escolha o destino
                <strong> &quot;Salvar como PDF&quot;</strong> ou <strong>&quot;Microsoft Print to PDF&quot;</strong>.
              </div>
            </>
          )}
        </div>

        {/* Footer / nav */}
        <div className="p-5 border-t border-slate-200 flex justify-between gap-2 bg-slate-50">
          <button
            onClick={() => (step > 1 ? setStep(step - 1) : onClose())}
            className="btn btn-secondary text-sm"
            data-testid="aep-wizard-back">
            {step > 1 ? <><i className="fa-solid fa-arrow-left"></i> Voltar</> : "Cancelar"}
          </button>
          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              className="btn btn-primary text-sm"
              data-testid="aep-wizard-next">
              Próximo <i className="fa-solid fa-arrow-right"></i>
            </button>
          ) : (
            <button onClick={onPrint} className="btn btn-primary text-sm" data-testid="aep-wizard-print">
              <i className="fa-solid fa-print"></i> Gerar PDF
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
